import React from 'react';
import axios from 'axios';

import fetchExaCare from '@/adapters/fetchExaCare';

import {
  assertSupportedS3UploadFiles,
  SupportedUploadContentTypes
} from './s3UploadFileTypes';

export interface S3File {
  name: string;
  key: string;
  size: number;
  mime_type: string;
  original_file: File;
}

export interface UploadProgressState {
  file?: File;
  loaded: number;
}

const S3_UPLOAD_TIMEOUT_MS = 2 * 60 * 1000;

export async function uploadFileToSignedUrl(
  file: File,
  signedUrl: string,
  setUploadProgress?: React.Dispatch<React.SetStateAction<UploadProgressState>>
) {
  await axios.put(signedUrl, file, {
    headers: { 'Content-Type': file.type || 'application/pdf' },
    timeout: S3_UPLOAD_TIMEOUT_MS,
    onUploadProgress: (event) => {
      const total = event.total ?? 0;
      if (total > 0) {
        setUploadProgress?.({ file, loaded: (event.loaded * 100) / total });
      }
    }
  });
}

export async function postS3Files(
  files: FileList | File[] | null,
  setUploadProgress?: React.Dispatch<React.SetStateAction<UploadProgressState>>,
  supportedContentTypes?: SupportedUploadContentTypes,
  uploadPurpose?: string
): Promise<S3File[]> {
  if (!files) {
    return [];
  }

  assertSupportedS3UploadFiles(files, supportedContentTypes);

  const s3files: S3File[] = [];

  try {
    // Can't loop through FileList with an iterator so have to use indexes
    for (let i = 0; i < files?.length; i += 1) {
      const file = files[i];
      if (!file) {
        continue;
      }
      const { signedUrl, key } = (await fetchExaCare.post('/upload-url', {
        extension: file.name.split('.').at(-1) ?? '',
        type: file.type,
        uploadPurpose
      })) as { signedUrl: string; key: string };
      await uploadFileToSignedUrl(file, signedUrl, setUploadProgress);
      s3files.push({
        name: file.name,
        key,
        size: file.size,
        mime_type: file.type,
        original_file: file
      });
    }
  } finally {
    setUploadProgress?.({} as UploadProgressState);
  }
  return s3files;
}
